import i18n from '@/utils/i18n';
import LostItemIcon from '@assets/icons/lost-item.svg';
import OverduePaymentIcon from '@assets/icons/overdue-payment.svg';
import React from 'react';
import { AnalyticsAlertType } from '@/types';
import { Badge, Icons } from '@usepepper/sprout-kit-ui';
import { createStyles } from '@/contexts/theme-context';
import { FONTS } from '@/styles';
import { formatCurrency } from '@/utils/money';
import { formatDate, momentWithTimezone } from '@/utils/date';
import { getTranslationValue } from '@/utils/translations';
import { ItemImage } from '@usepepper/ui/atoms/item-image';
import { SalesAlert } from '@/experiences/sales-portal/domain/accounts/screens/account-detail/blocs/types';
import { Spacing } from '@usepepper/sprout-kit';
import { Text, View } from 'react-native';
import { Typography, TypographyVariant } from '@usepepper/ui/atoms/typography';
import { useIsMobileBottomNavLayout } from '@sales-portal/blocs/hooks/use-is-mobile-bottom-nav-layout';

interface AccountDetailSalesAlertCardProps {
  alert: SalesAlert;
  isLast: boolean;
}

const ICON_SIZE = 16;
const ICON_STROKE_WIDTH = 1.5;

export const AccountDetailSalesAlertCard: React.FC<
  AccountDetailSalesAlertCardProps
> = ({ alert, isLast }) => {
  const [styles, theme] = useStyles();
  const isMobileBottomNavLayout = useIsMobileBottomNavLayout();

  const subtitleVariant: TypographyVariant = 'paragraph-md';

  const renderCardContent = () => {
    switch (alert.alertType) {
      case AnalyticsAlertType.ChurnRisk:
        return (
          <>
            <Badge
              color="muted"
              icon={
                <Icons.CircleDotDashed
                  color={theme.sk.color.icon.primary}
                  size={ICON_SIZE}
                  strokeWidth={ICON_STROKE_WIDTH}
                />
              }
              size="$lg"
              variant="outline"
            >
              <Text style={styles.badgeLabel}>
                {i18n.t('sales_portal.account_detail.sales_alerts.churn_risk')}
              </Text>
            </Badge>
            <Typography
              color={theme.sk.color.text.primary}
              variant={subtitleVariant}
            >
              {getTranslationValue(alert.subtitle)}
            </Typography>
          </>
        );
      case AnalyticsAlertType.LostItem: {
        const subtitle = (
          <Typography
            color={theme.sk.color.text.primary}
            variant={subtitleVariant}
          >
            {getTranslationValue(alert.subtitle)}
          </Typography>
        );
        const productDetails = (
          <View
            style={[
              styles.productRow,
              !isMobileBottomNavLayout ? null : styles.productRowMobile,
            ]}
          >
            <ItemImage
              alt={alert.displayName}
              imageURLs={alert.photoUrlList ?? []}
              size={Spacing.SPACE_2XLARGE}
            />
            <View style={styles.productInfo}>
              <Typography numberOfLines={1} variant="paragraph-md-600">
                {alert.displayName}
              </Typography>
              <Typography
                color={theme.sk.color.text.tertiary}
                numberOfLines={1}
                variant="paragraph-sm"
              >
                {alert.packUnit}
              </Typography>
            </View>
          </View>
        );

        return (
          <>
            <Badge
              color="muted"
              icon={
                <LostItemIcon
                  height={ICON_SIZE}
                  stroke={theme.sk.color.icon.primary}
                  width={ICON_SIZE}
                />
              }
              size="$lg"
              variant="outline"
            >
              <Text style={styles.badgeLabel}>
                {i18n.t('sales_portal.account_detail.sales_alerts.lost_item')}
              </Text>
            </Badge>
            {!isMobileBottomNavLayout ? (
              <>
                {productDetails}
                {subtitle}
              </>
            ) : (
              <>
                {subtitle}
                {productDetails}
              </>
            )}
          </>
        );
      }
      case AnalyticsAlertType.OverduePayment:
        return (
          <>
            <Badge
              color="muted"
              icon={
                <OverduePaymentIcon
                  height={ICON_SIZE}
                  stroke={theme.sk.color.icon.primary}
                  width={ICON_SIZE}
                />
              }
              size="$lg"
              variant="outline"
            >
              <Text style={styles.badgeLabel}>
                {i18n.t(
                  'sales_portal.account_detail.sales_alerts.overdue_payment',
                )}
              </Text>
            </Badge>
            <Typography
              color={theme.sk.color.text.primary}
              variant={subtitleVariant}
            >
              {getTranslationValue(alert.subtitle)}
            </Typography>
            {alert.data.map((invoice) => (
              <View key={invoice.alertUUID} style={styles.invoiceRow}>
                <View>
                  <Typography variant="paragraph-md-600">
                    {invoice.invoiceNumber}
                  </Typography>
                  <Typography
                    color={theme.sk.color.text.tertiary}
                    variant="paragraph-sm"
                  >
                    {i18n.t(
                      'sales_portal.account_detail.sales_alerts.due_date',
                      {
                        date: formatDate(momentWithTimezone(invoice.dueAt)),
                      },
                    )}
                  </Typography>
                </View>
                <Typography
                  color={theme.sk.color.text.primary}
                  variant="paragraph-md-600"
                >
                  {formatCurrency(invoice.amount, null, {
                    currencyDisplay: 'narrowSymbol',
                  })}
                </Typography>
              </View>
            ))}
          </>
        );
    }
  };

  return (
    <View
      style={[
        styles.card,
        !isMobileBottomNavLayout ? null : styles.cardMobile,
        isLast ? styles.cardLast : null,
      ]}
    >
      {renderCardContent()}
    </View>
  );
};

const useStyles = createStyles((theme) => ({
  badgeLabel: {
    ...FONTS.primarySemiBold,
    color: theme.sk.color.text.primary,
    fontSize: theme.sk.typography.paragraph.md.fontSize,
  },
  card: {
    borderBottomColor: theme.sk.color.divider.default,
    borderBottomWidth: 1,
    gap: theme.sk.spacing.spaceSmall,
    paddingBottom: theme.sk.spacing.spaceMedium,
  },
  cardLast: {
    borderBottomWidth: 0,
  },
  cardMobile: {
    gap: theme.sk.spacing.spaceSMedium,
  },
  invoiceRow: {
    alignItems: 'flex-start',
    borderColor: theme.sk.color.border.primary,
    borderRadius: theme.sk.radii.radiusMedium,
    borderWidth: 1,
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingHorizontal: theme.sk.spacing.spaceSMedium,
    paddingVertical: theme.sk.spacing.spaceSmall,
  },
  productInfo: {
    flex: 1,
    justifyContent: 'center',
  },
  productRow: {
    alignItems: 'center',
    flexDirection: 'row',
    gap: theme.sk.spacing.spaceSMedium,
  },
  productRowMobile: {
    backgroundColor: theme.sk.color.bg.primary.default,
    borderColor: theme.sk.color.border.primary,
    borderRadius: theme.sk.radii.radiusLarge,
    borderWidth: 1,
    gap: theme.sk.spacing.spaceMedium,
    padding: theme.sk.spacing.spaceSMedium,
  },
}));
